"use client";

import { FC, useEffect, useRef } from "react";

import { EmbedSectionRecord } from "@graphql/types/graphql";
import s from "./EmbedSection.module.scss";

interface Props {
  data: EmbedSectionRecord;
}

/**
 * Renders CMS-authored embed markup (Calendly, HubSpot meetings, iframes, ...).
 *
 * The markup is injected on the client rather than server-rendered because
 * browsers never execute `<script>` tags that arrive via `innerHTML`. Rendering
 * server-side only appears to work on a cold load, where the HTML parser runs
 * the scripts; on a client-side navigation React sets `innerHTML` and the
 * scripts stay inert, leaving the embed blank.
 */
export const EmbedSection: FC<Props> = ({ data: { embed } }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !embed?.length) return undefined;

    container.innerHTML = embed;

    Array.from(container.querySelectorAll("script")).forEach((inertScript) => {
      const script = document.createElement("script");

      Array.from(inertScript.attributes).forEach(({ name, value }) => {
        script.setAttribute(name, value);
      });
      script.text = inertScript.text;

      // Dynamically created scripts default to async, which would let an inline
      // initialiser run before the vendor script it depends on. Opting out
      // restores the document-order execution the embed was written against.
      if (!inertScript.hasAttribute("async")) script.async = false;

      inertScript.replaceWith(script);
    });

    return () => {
      container.innerHTML = "";
    };
  }, [embed]);

  if (!embed?.length) return null;

  return <div className={s.EmbedSection} ref={containerRef} />;
};
